import React, { useState } from 'react'
import { NextPage } from 'next'
import PublicLayout from 'src/components/Layout/public'
import Container from 'src/components/Common/Container'
import { Section } from 'src/components/Common/Section'
import { BannerContainer, Content, FormContainer, Grid } from 'src/components/Contact/styles'
import { FormProvider } from 'src/components/Common/Form'
import { useFormik } from 'formik'
import Field from 'src/components/Common/InputContainer'
import { Button } from 'src/components/Common/Button'
import Link from 'next/link'
import { BannerBeStudent } from 'src/components/Common/BannerBeStudent'
import initialValues, { ContactShape } from 'src/shapes/contact'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import subject from 'src/components/Contact/contant/subject'
import Turnstile, { useTurnstile } from 'react-turnstile'
import { CategoriesApi } from 'src/service/api/api.types'
import { apiPublic } from 'src/service/api/api.public'
import { setError } from 'src/store/errors'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useErrors } from 'src/hooks/useErrors'
import { Title } from 'src/components/Common'
import { colors } from 'src/theme/colors'
import TitleWrapper from 'src/components/Common/TitleWrapper'
import { State } from 'src/store/rootReducers'
import { WhatsAppTextWrapper } from 'src/components/Common/Whatsapp/Whatsapp.styles'
import { WhatsApp } from 'src/components/Common/Whatsapp/Whastapp.component'

interface Props {
  categories: CategoriesApi
}

const ContactPage: NextPage<Props> = () => {
  const isAuthenticated = useSelector((state: State) => state.user.isAuthenticated)
  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [turnstileToken, setTurnstileToken] = useState<string>('')
  const turnstile = useTurnstile()

  const handleTurnstileVerify = React.useCallback(
    async values => {
      if (!turnstileToken && process?.env?.NEXT_PUBLIC_CAPTCHA_SITE_KEY) {
        // eslint-disable-next-line no-console
        console.error('Turnstile token not available')
        return
      }

      const res = await apiPublic.sendContact({
        payload: values,
        captcha: turnstileToken,
      })

      if (res.kind === 'ok') {
        toast(t('toast.submitContactSuccess'), { type: 'success' })
        setTurnstileToken('') // Reset token
      } else {
        dispatch(
          setError({
            response: res,
          }),
        )

        turnstile.reset()
      }
    },
    [turnstileToken],
  )

  useErrors()

  const formik = useFormik({
    initialValues,
    onSubmit: async values => {
      setIsLoading(true)
      await handleTurnstileVerify(values)
      setIsLoading(false)
    },
    validationSchema: Yup.object({
      name: Yup.string().min(2, t('validation.name.min')).required(t('validation.required')),
      email: Yup.string().email(t('validation.email.matches')).required(t('validation.required')),
      subject: Yup.string().required(t('validation.required')),
      message: Yup.string().required(t('validation.required')),
    }),
  })

  return (
    <PublicLayout showCategory={false}>
      <TitleWrapper>
        <Title
          borderMargin="medium"
          size="lg"
          borderColor={colors.palette.white}
          tx={t('contact.title')}
          theme="light"
        />
      </TitleWrapper>
      <Container>
        <Content>
          <Section padding="0 0 0.5rem" paddingMobile="0rem 0.3rem">
            <Grid>
              <FormContainer>
                <WhatsAppTextWrapper>
                  <h2>{t('contact.contactTel')}</h2>
                  <h3>{t('contact.contactWppDisclaimer')}</h3>
                  <WhatsApp />
                </WhatsAppTextWrapper>

                <div className="text-container">
                  <p>
                    {t('contact.contactEmail')}{' '}
                    <Link legacyBehavior passHref href="mailto:cordasemusica@gmail.com">
                      <a>cordasemusica@gmail.com</a>
                    </Link>
                  </p>
                </div>

                <br />

                <div className="form-content">
                  <FormProvider<ContactShape> provider={formik}>
                    <Field
                      required
                      name="name"
                      component="input"
                      preset="primary"
                      label={t('contact.form.name')}
                    />
                    <Field
                      required
                      name="email"
                      component="input"
                      label={t('contact.form.email')}
                    />
                    <Field
                      required
                      name="subject"
                      component="select"
                      options={subject}
                      label={t('contact.form.subject')}
                    />
                    <Field
                      required
                      name="message"
                      component="textarea"
                      cols="40"
                      rows="10"
                      label={t('contact.form.body')}
                    />
                    <Turnstile
                      sitekey={process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY || ''}
                      onVerify={token => setTurnstileToken(token)}
                      onError={() => setTurnstileToken('')}
                      onExpire={() => setTurnstileToken('')}
                      theme="light"
                    />
                    <Button
                      isLoading={isLoading}
                      type="submit"
                      preset="form"
                      labelTx={t('contact.form.btn')}
                    />
                  </FormProvider>
                </div>
              </FormContainer>
              <BannerContainer>
                {isAuthenticated === false ? <BannerBeStudent /> : null}
              </BannerContainer>
            </Grid>
          </Section>
        </Content>
      </Container>
    </PublicLayout>
  )
}

export default ContactPage
